import { useLazyQuery } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo } from 'react';

import { Box, Button, EntryList, Grid, Icon, Loader, Text } from '~/components/blocks';
import { ActiveText } from '~/components/pages/Settings_Organization_WebBookingSettings/ActiveText';
import {
  GetWebBookingsSettingDocument,
  Practitioner,
  WebBookingsSettingFragment,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import {
  editSettingDrawerState,
  materialsModalState,
} from '~/state/settings_organization_webbookingsettings/atoms';
import { formatBusinessHoursLines } from '~/utils/organization_business_hour';
import { publicPath } from '~/utils/path';

import { EditWebBookingSettingDrawer } from './EditWebBookingSettingDrawer';
import { MaterialsInfo } from './MaterialsInfo';
import { MaterialsModal } from './MaterialsModal';

export const formatWebBookingsSetting = (loginPractitioner: Practitioner) => {
  const setting = loginPractitioner.organization.webBookingsSetting
    ? (loginPractitioner.organization.webBookingsSetting as WebBookingsSettingFragment)
    : null;

  if (!setting) return null;

  return {
    published: setting.published,
    telemedicine: setting.telemedicine,
    facetoface: setting.facetoface,
    description: setting.description,
    logo: setting.logo ? setting.logo?.url : process.env.defaultLogoPath,
    website: setting.website,
    openingHours: setting.openingHours,
    electronicPrescription: setting.electronicPrescription,
    onlineQualificationConfirmation: setting.onlineQualificationConfirmation,
  };
};

export const WebBookingSettings = () => {
  const theme = useTheme();
  const setMaterialsModalState = useSetAtom(materialsModalState);
  const [getWebBookingsSetting, { data, loading }] = useLazyQuery(GetWebBookingsSettingDocument, {
    fetchPolicy: 'network-only',
  });
  const setDrawerState = useSetAtom(editSettingDrawerState);

  const { verifyRole } = usePractitionerRole();
  const setting = useMemo(
    () => (data?.me ? formatWebBookingsSetting(data?.me as Practitioner) : null),
    [data?.me],
  );

  const organizationBusinessHours = useMemo(
    () => (data ? (getMe(data)?.organization?.organizationBusinessHours ?? []) : []),
    [data],
  );

  const businessHoursLines = useMemo(
    () => formatBusinessHoursLines(organizationBusinessHours),
    [organizationBusinessHours],
  );

  const handleClick = useCallback(() => {
    setDrawerState(() => ({ isOpen: true, shouldScrollToBusinessHours: false }));
  }, [setDrawerState]);

  const handleClickModalOpen = useCallback(() => {
    setMaterialsModalState(() => ({ isOpen: true }));
  }, [setMaterialsModalState]);

  useEffect(() => {
    getWebBookingsSetting();
  }, [getWebBookingsSetting]);

  return (
    <>
      {!setting || loading ? (
        <Loader open inside />
      ) : (
        <>
          {setting.published && (
            <Box mb={theme.space.l} width="100%">
              <MaterialsInfo isOpen={handleClickModalOpen} />
            </Box>
          )}
          <Grid overflow="auto" gridTemplateColumns="1fr min-content" gridGap={theme.space.xxl}>
            <Grid
              height="100%"
              overflow="auto"
              gridTemplateColumns="min-content 1fr"
              gridGap={theme.space.xxl}
            >
              <img
                width="120"
                src={setting?.logo || process.env.defaultLogoPath || publicPath('/logo.svg')}
              />
              <Box height="100%" overflow="auto">
                <EntryList>
                  <EntryList.Head>店舗公開</EntryList.Head>
                  <EntryList.Body>
                    <Text whiteSpace="pre-wrap" wordBreak="break-all">
                      {setting?.published ? <Text color="green">公開</Text> : <Text>非公開</Text>}
                    </Text>
                  </EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>電子処方箋対応</EntryList.Head>
                  <EntryList.Body>
                    <ActiveText active={setting.electronicPrescription} />
                  </EntryList.Body>
                </EntryList>
                {setting?.published && (
                  <>
                    <EntryList>
                      <EntryList.Head>オンライン資格確認対応（オンライン服薬指導）</EntryList.Head>
                      <EntryList.Body>
                        <ActiveText active={setting.onlineQualificationConfirmation} />
                      </EntryList.Body>
                    </EntryList>
                    <EntryList>
                      <EntryList.Head>オンライン服薬指導</EntryList.Head>
                      <EntryList.Body>
                        <ActiveText active={setting.telemedicine} />
                      </EntryList.Body>
                    </EntryList>
                    <EntryList>
                      <EntryList.Head>処方箋ネット受付</EntryList.Head>
                      <EntryList.Body>
                        <ActiveText active={setting.facetoface} />
                      </EntryList.Body>
                    </EntryList>
                  </>
                )}
                <EntryList>
                  <EntryList.Head>紹介文</EntryList.Head>
                  <EntryList.Body>
                    <Text whiteSpace="pre-wrap" wordBreak="break-all">
                      {setting?.description || '未設定'}
                    </Text>
                  </EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>ホームページURL</EntryList.Head>
                  <EntryList.Body>
                    <Text wordBreak="break-all">{setting?.website || '未設定'}</Text>
                  </EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>営業時間</EntryList.Head>
                  <EntryList.Body>
                    {businessHoursLines ? (
                      <>
                        <Text block whiteSpace="pre-wrap" wordBreak="break-all">
                          {businessHoursLines}
                        </Text>
                        {setting?.openingHours && (
                          <Text block whiteSpace="pre-wrap" wordBreak="break-all" mt="4px">
                            {setting.openingHours}
                          </Text>
                        )}
                      </>
                    ) : (
                      <Text whiteSpace="pre-wrap" wordBreak="break-all">
                        {setting?.openingHours || '未設定'}
                      </Text>
                    )}
                  </EntryList.Body>
                </EntryList>
              </Box>
            </Grid>
            <MaterialsModal />
            <Box>
              <Button disabled={!verifyRole('admin')} onClick={handleClick}>
                <Icon icon="edit" size="m" />
                編集
              </Button>
            </Box>
          </Grid>
        </>
      )}
      <EditWebBookingSettingDrawer />
    </>
  );
};
